import { ApolloLink, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { ApolloClient, InMemoryCache } from '@apollo/client-integration-nextjs';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { GraphQLError } from 'graphql';

import { CONFIG } from '@/config';
import { STATUS_CODES } from '@/constants/common';
import { logGraphQLErrors } from '@/utils/apollo/log-graphql-error';
import { CookiesUtils } from '@/utils/helpers/cookies';
import { hostname } from '@/utils/helpers/locales';

const { SUCCESS, AUTHENTICATION_ERROR } = STATUS_CODES;

const safeGraphqlFetch: typeof fetch = async (input, init) => {
  const response = await fetch(input, init);
  const rawBody = await response.text();
  const contentType = response.headers.get('content-type') ?? '';
  const isJsonResponse =
    contentType.includes('application/json') ||
    contentType.includes('application/graphql-response+json');

  if (rawBody.trim().length > 0 && isJsonResponse) {
    return new Response(rawBody, {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers
    });
  }

  const fallbackBody = JSON.stringify({
    data: null,
    errors: [
      {
        message: `Invalid GraphQL response: ${
          rawBody.trim().length === 0 ? 'empty body' : 'non-JSON body'
        }`,
        extensions: {
          code: 'INVALID_GRAPHQL_RESPONSE',
          httpStatus: response.status
        }
      }
    ]
  });

  // Per the GraphQL-over-HTTP spec, `application/graphql-response+json` tells
  // the transport (Apollo Client / apollo-upload-client) to parse the body as
  // a GraphQL response regardless of the HTTP status. Without this header
  // Apollo would surface non-2xx + `application/json` as a network ServerError
  // and the synthesized `errors[]` (incl. INVALID_GRAPHQL_RESPONSE) would
  // never reach `graphQLErrors` in `onError`. The upstream status is kept
  // intact so logs/metrics still see the real failure.
  const fallbackHeaders = new Headers(response.headers);
  fallbackHeaders.set(
    'content-type',
    'application/graphql-response+json; charset=utf-8'
  );

  return new Response(fallbackBody, {
    status: response.status,
    statusText: response.statusText,
    headers: fallbackHeaders
  });
};

export function makeClient(locale: string, ssrCookie?: string) {
  const uploadLink = createUploadLink({
    uri: `https://${hostname(locale, CONFIG.host)}/graphql`,
    headers: {
      'Apollo-Require-Preflight': 'true'
    },
    credentials: 'include',
    fetch: safeGraphqlFetch
  });

  const statusMiddleware = new ApolloLink((operation, forward) =>
    forward(operation).map(response => {
      const data = response?.data;
      const isMutation = operation.query.definitions.some(
        def =>
          def.kind === 'OperationDefinition' && def.operation === 'mutation'
      );

      if (data && isMutation) {
        Object.keys(data).forEach(key => {
          const result = data[key];
          if (result?.status && result.status !== SUCCESS) {
            response.errors = [
              new GraphQLError(
                `Mutation failed with status: ${result.status}`,
                {
                  extensions: {
                    code: result.status,
                    lockExpiresAt: result?.lockExpiresAt,
                    isStatusError: true
                  }
                }
              )
            ];
            response.data = null;
          }
        });
      }

      return response;
    })
  );

  // TODO: Replace with new error format after upgrade to Apollo Client 4.0
  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      const authError = graphQLErrors.some(
        error => error.extensions?.code === AUTHENTICATION_ERROR
      );

      if (authError) {
        CookiesUtils.logout();
      }

      logGraphQLErrors(graphQLErrors);
    }

    if (networkError) console.error(`[Network error]: ${networkError}`);
  });

  const authLink = setContext((_, { headers }) => {
    const cookie = ssrCookie || headers?.cookie;

    return {
      headers: {
        ...headers,
        ...(cookie ? { cookie } : {})
      }
    };
  });

  const operationNameLink = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        'X-APOLLO-OPERATION-NAME': operation.operationName || ''
      }
    }));

    return forward(operation);
  });

  return new ApolloClient({
    devtools: {
      enabled: process.env.NODE_ENV === 'development'
    },
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            profile: {
              merge: true
            },
            ads: {
              keyArgs: ['state']
            },
            vacancies: {
              merge: true
            },
            resumes: {
              merge: true
            }
          }
        },
        Resume: {
          fields: {
            paidServices: {
              merge: false
            }
          }
        },
        Vacancy: {
          fields: {
            paidServices: {
              merge: false
            }
          }
        }
      }
    }),
    link: from([
      errorLink,
      statusMiddleware,
      authLink,
      operationNameLink,
      uploadLink
    ])
  });
}
